import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MenuIcon from '@mui/icons-material/Menu';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { useAuth } from '../providers/AuthProvider';
import { ROUTES } from '../routes';

const drawerWidth = 260;

export default function PartnerLayout({ children }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [accountAnchor, setAccountAnchor] = React.useState(null);
  const userName = auth?.user?.full_name || auth?.user?.name || auth?.user?.email || 'Partner';

  const closeMobileNav = () => setMobileOpen(false);
  const goTo = (target) => {
    closeMobileNav();
    if (target === 'portfolio') {
      if (location.pathname !== ROUTES.PARTNER) { navigate(ROUTES.PARTNER); return; }
      document.getElementById('client-portfolio')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    navigate(ROUTES.PARTNER);
  };
  const logout = () => {
    setAccountAnchor(null);
    navigate('/logout', { replace: true });
  };

  const navItems = [
    { key: 'overview', label: 'Dashboard', icon: <DashboardIcon />, onClick: () => goTo('overview') },
    { key: 'portfolio', label: 'Client Portfolio', icon: <BusinessIcon />, onClick: () => goTo('portfolio') },
  ];

  const sideNav = (
    <Box sx={{ px: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography
        variant="caption"
        sx={{ px: 1, pt: 1.5, pb: 1, color: '#64748b', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}
      >
        Partner workspace
      </Typography>
      <List sx={{ p: 0 }}>
        {navItems.map((item) => {
          const selected = item.key === 'overview' ? location.pathname === ROUTES.PARTNER : false;
          return (
            <ListItemButton
              key={item.key}
              selected={selected}
              onClick={item.onClick}
              sx={{ mb: 0.5, borderRadius: 1.5, minHeight: 46, '&.Mui-selected': { bgcolor: '#eaf3ff', color: '#0b4ea2' }, '&.Mui-selected:hover': { bgcolor: '#dbeafe' } }}
            >
              <ListItemIcon sx={{ minWidth: 38, color: selected ? '#0b4ea2' : '#64748b' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: selected ? 700 : 600 }} />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ mt: 'auto', pb: 2 }}>
        <Divider sx={{ mb: 1.25 }} />
        <ListItemButton onClick={logout} sx={{ borderRadius: 1.5, minHeight: 46, color: '#475569', '&:hover': { bgcolor: '#f8fafc', color: '#0f172a' } }}>
          <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}><ExitToAppIcon /></ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 700 }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#fafafa' }}>
      <AppBar position="fixed" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e2e8f0', zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 70 }, px: { xs: 2, sm: 3 } }}>
          {isMobile ? <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 2, color: '#475569' }} aria-label="Open navigation"><MenuIcon /></IconButton> : null}
          <Box role="button" aria-label="Go to partner dashboard" onClick={() => goTo('overview')} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <Box component="img" src="/site.png" alt="Susha Asset Management" sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, objectFit: 'contain', mr: { xs: 0, sm: 2 } }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1.25rem', display: { xs: 'none', sm: 'block' } }}>Susha Asset Management</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ textAlign: 'right', mr: 1, minWidth: 0, display: { xs: 'none', sm: 'block' }, maxWidth: 260 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</Typography>
            <Typography variant="caption" color="#64748b">Partner</Typography>
          </Box>
          <IconButton onClick={(event) => setAccountAnchor(event.currentTarget)} size="small" aria-label="Open account menu">
            <Avatar sx={{ width: 36, height: 36, bgcolor: '#3b82f6', color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>{String(userName).charAt(0).toUpperCase()}</Avatar>
          </IconButton>
          <Menu
            anchorEl={accountAnchor}
            open={Boolean(accountAnchor)}
            onClose={() => setAccountAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: 1, minWidth: 220, borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' } }}
          >
            <MenuItem onClick={() => { setAccountAnchor(null); navigate(ROUTES.CHANGE_PASSWORD); }} sx={{ py: 1.4 }}><VpnKeyOutlinedIcon fontSize="small" sx={{ mr: 1.5, color: '#64748b' }} />Change password</MenuItem>
            <Divider />
            <MenuItem onClick={logout} sx={{ py: 1.4 }}><ExitToAppIcon fontSize="small" sx={{ mr: 1.5, color: '#64748b' }} />Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={closeMobileNav}
        ModalProps={{ keepMounted: true }}
        sx={{ width: drawerWidth, flexShrink: 0, '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', border: 'none', borderRight: '1px solid #e2e8f0', bgcolor: '#ffffff', pt: { xs: '56px', sm: '70px' } } }}
      >
        {sideNav}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` }, mt: { xs: '56px', sm: '70px' }, p: { xs: 2, sm: 3 }, bgcolor: '#fafafa' }}>
        {children}
      </Box>
    </Box>
  );
}
